import Icon from 'components/Icon';
import { IntrinsicSizeImg } from 'components/Img';
import { AnchorLink, OptionalLink } from 'components/Link';
import Message from 'components/Message';
import RichText from 'components/RichText';
import Text from 'components/Text';
import { PRODUCT_DETAILS_IMPORTANT_INFO_ID } from 'constants/ids';
import type { Product } from 'models/product';
import { getBrandLogoImage } from 'utils/business-logic';
import { filterTruthy } from 'utils/collection';
import { is } from 'utils/helpers';

import {
	filterAdditionalTexts,
	hasChemicalInformation,
} from './helpers/productDataHelpers';
import WarningSymbols from './WarningSymbols';

interface Props {
	aboutSectionHeading: string;
	bundleListHeading: string;
	descriptionClassName?: string;
	onSafetyInstructionsClick: () => void;
	product: Product;
	showSafetyInstructionsButtonText: string;
}

export default function ProductDetailsDescription({
	aboutSectionHeading,
	bundleListHeading,
	descriptionClassName,
	onSafetyInstructionsClick,
	product,
	showSafetyInstructionsButtonText,
}: Props) {
	const { brand, chemicalInformationResponse, otherAssets, usPs } = product;
	const brandLogoImage = getBrandLogoImage(product);
	const brandLogoAlt = brandLogoImage?.alt || brand?.title;
	const warningImages = chemicalInformationResponse?.hazardPictogram?.map(
		(pictogram) => pictogram.image,
	);
	const productInformationSymbols = otherAssets?.filter(
		(asset) =>
			asset.subType === 'ProductInformationSVGSymbol' &&
			asset.formatName === 'SVG',
	);

	const additionalTexts = filterTruthy(
		filterAdditionalTexts(product.additionalTexts).filter((text) =>
			['Notification', 'Offer'].includes(text.type),
		),
		'textFormatted',
	);

	return (
		<>
			<Text as="h2" text={aboutSectionHeading} />

			<div className="my-r-md flex flex-wrap-reverse items-end justify-between gap-4">
				{is.arrayWithLength(usPs) && (
					<ul className="inline-flex list-inside list-disc flex-col gap-2 rounded-md bg-subtle p-4 font-bold">
						{usPs.map((usp) => (
							<li key={usp}>{usp}</li>
						))}
					</ul>
				)}
				{brandLogoImage && (
					<OptionalLink
						href={brand?.relativeUrl}
						isScreenReaderHidden={!brandLogoAlt}
					>
						<IntrinsicSizeImg
							asset={brandLogoImage}
							alt={brandLogoAlt}
							className="max-h-16 w-auto max-w-40"
						/>
					</OptionalLink>
				)}
			</div>

			{is.arrayWithLength(productInformationSymbols) && (
				<div className="mb-r-md flex flex-wrap items-start gap-4">
					{productInformationSymbols.map((asset) => (
						<IntrinsicSizeImg
							key={asset.src}
							asset={asset}
							className="h-14 w-auto"
						/>
					))}
				</div>
			)}
			{additionalTexts.map((text) => (
				<Message
					key={text.id}
					variant="information"
					heading={text.title}
					className="mb-2 max-w-100"
					text={text.textFormatted}
				/>
			))}
			<RichText
				className={descriptionClassName}
				html={product.webTextFormatted}
			/>

			{is.arrayWithLength(product.bundleVariantTexts) && (
				<>
					<p className="mt-4">{bundleListHeading}</p>
					<ul className="list-inside list-disc">
						{product.bundleVariantTexts.map((text) => (
							<li key={text}>{text}</li>
						))}
					</ul>
				</>
			)}
			{is.arrayWithLength(warningImages) && (
				<div className="mt-r-md">
					<WarningSymbols
						heading={chemicalInformationResponse?.signalWord}
						text={chemicalInformationResponse?.hazardStatements}
						images={warningImages}
					/>
				</div>
			)}
			{hasChemicalInformation(product) && (
				<p className="mt-4">
					<AnchorLink
						underline
						anchor={PRODUCT_DETAILS_IMPORTANT_INFO_ID}
						onClick={onSafetyInstructionsClick}
					>
						{showSafetyInstructionsButtonText}
						<Icon name="chevron-down" className="ml-1" />
					</AnchorLink>
				</p>
			)}
		</>
	);
}
ProductDetailsDescription.displayName = 'ProductDetailsDescription';
